import AddonService from '@/services/AddonService';
import { errorHandler } from "@/services/errorHandler";

export default {
  namespaced: true,
  state: {
    addons: [],
    productAddons: {},
    storeAddons: {},
    loading: false
  },
  mutations: {
    SET_ADDONS(state, addons) {
      state.addons = addons;
    },
    SET_PRODUCT_ADDONS(state, { productId, addons }) {
      state.productAddons = {
        ...state.productAddons,
        [productId]: addons
      };
    },
    SET_STORE_ADDONS(state, { storeId, addons }) {
      state.storeAddons = {
        ...state.storeAddons,
        [storeId]: addons
      };
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    }
  },
  actions: {
    // Addon actions
    async fetchAllAddons({ commit }) {
      commit('SET_LOADING', true);
      try {
        const addons = await AddonService.getAllAddons();
        commit('SET_ADDONS', addons);
        return addons;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async fetchAddonById({ state }, id) {
      try {
        return await AddonService.getAddonById(id);
      } catch (error) {
        await errorHandler.tomcatError(error);
        return null;
      }
    },
    
    async createAddon({ dispatch }, addon) {
      try {
        const createdAddon = await AddonService.createAddon(addon);
        await dispatch('fetchAllAddons');
        return createdAddon;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return null;
      }
    },
    
    async updateAddon({ dispatch }, { id, addon }) {
      try {
        const updatedAddon = await AddonService.updateAddon(id, addon);
        await dispatch('fetchAllAddons');
        return updatedAddon;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return null;
      }
    },
    
    async deleteAddon({ dispatch }, id) {
      try {
        await AddonService.deleteAddon(id);
        await dispatch('fetchAllAddons');
        return true;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return false;
      }
    },
    
    // Product Addon actions
    async fetchAddonsByProductId({ commit }, productId) {
      commit('SET_LOADING', true);
      try {
        const addons = await AddonService.getAddonsByProductId(productId);
        commit('SET_PRODUCT_ADDONS', { productId, addons });
        return addons;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async addAddonToProduct({ dispatch }, { productId, addonId }) {
      try {
        await AddonService.addAddonToProduct(productId, addonId);
        await dispatch('fetchAddonsByProductId', productId);
        return true;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return false;
      }
    },
    
    async removeAddonFromProduct({ dispatch }, { productId, addonId }) {
      try {
        await AddonService.removeAddonFromProduct(productId, addonId);
        await dispatch('fetchAddonsByProductId', productId);
        return true;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return false;
      }
    },
    
    // Store Addon actions
    async fetchAddonsByStoreId({ commit }, storeId) {
      commit('SET_LOADING', true);
      try {
        const addons = await AddonService.getAddonsByStoreId(storeId);
        commit('SET_STORE_ADDONS', { storeId, addons });
        return addons;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async addAddonToStore({ dispatch }, { storeId, addonId }) {
      try {
        await AddonService.addAddonToStore(storeId, addonId);
        await dispatch('fetchAddonsByStoreId', storeId);
        return true;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return false;
      }
    },
    
    async removeAddonFromStore({ dispatch }, { storeId, addonId }) {
      try {
        await AddonService.removeAddonFromStore(storeId, addonId);
        await dispatch('fetchAddonsByStoreId', storeId);
        return true;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return false;
      }
    },
    
    // Product Addons query action
    async fetchAddonsByProductIdQuery({ commit }, productId) {
      commit('SET_LOADING', true);
      try {
        const addons = await AddonService.getAddonsByProductIdQuery(productId);
        commit('SET_PRODUCT_ADDONS', { productId, addons });
        return addons;
      } catch (error) {
        await errorHandler.tomcatError(error);
        return [];
      } finally {
        commit('SET_LOADING', false);
      }
    }
  },
  getters: {
    getAllAddons: state => state.addons,
    getAddonById: state => id => state.addons.find(addon => addon.id === id),
    getProductAddons: state => productId => state.productAddons[productId] || [],
    getStoreAddons: state => storeId => state.storeAddons[storeId] || [],
    isLoading: state => state.loading
  }
};